import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ScaleIcon from '@mui/icons-material/Scale';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import StoreIcon from '@mui/icons-material/Store';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import CompassCalibrationIcon from '@mui/icons-material/CompassCalibration';
import MapIcon from '@mui/icons-material/Map';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import RvHookupIcon from '@mui/icons-material/RvHookup';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';

import { getDashboardSummary } from '../../services/loads/loadsService';
import { isNavItemHidden } from '../../hooks/usePermission';
import { useEntityLabels } from '../../hooks/useEntityLabels';
import FormPageScroll from '../../components/FormPageScroll';

const BRAND_RED = '#E2001A';

const shortcuts = [
    { label: 'Weighing', description: 'Open the weighing console', path: '/loads/weighing', icon: <ScaleIcon />, permKey: 'loads.weighing' },
    { label: 'Completed loads', description: 'View completed load history', path: '/loads/completed', icon: <CheckCircleOutlineIcon />, permKey: 'loads.completed' },
    { label: 'Accounts', description: 'Customer and account master data', path: '/accounts', icon: <AccountBoxIcon />, permKey: 'accounts' },
    { label: 'Commodities', description: 'Products and materials', path: '/commodities', icon: <StoreIcon />, permKey: 'commodities' },
    { label: 'Hauliers', description: 'Haulage companies', path: '/hauliers', icon: <LocalShippingIcon />, permKey: 'hauliers' },
    { label: 'Drivers', description: 'Driver records', path: '/drivers', icon: <PersonPinCircleIcon />, permKey: 'drivers' },
    { label: 'Locations', description: 'Sites, stores, and depots', path: '/locations', icon: <CompassCalibrationIcon />, permKey: 'locations' },
    { label: 'LA Areas', description: 'Local authority areas', path: '/la-areas', icon: <MapIcon />, permKey: 'laAreas' },
    { label: 'Contracts', description: 'Customer contracts', path: '/contracts', icon: <HistoryEduIcon />, permKey: 'contracts' },
    { label: 'Vehicles', description: 'Vehicle register', path: '/vehicles', icon: <AirportShuttleIcon />, permKey: 'vehicles' },
    { label: 'Trailers', description: 'Trailer register', path: '/trailers', icon: <RvHookupIcon />, permKey: 'trailers' },
    { label: 'Reports', description: 'Operational reports', path: '/reports', icon: <AssessmentIcon />, permKey: 'reports' },
    { label: 'Settings', description: 'Site and weighing configuration', path: '/settings/general', icon: <SettingsIcon />, permKey: 'settings' },
];

function StatCard({ title, value, subtitle, icon, accent = BRAND_RED, onClick }) {
    const content = (
        <CardContent sx={{ p: 2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ color: accent, opacity: 0.9 }}>{icon}</Box>
            </Stack>
        </CardContent>
    );

    if (!onClick) {
        return (
            <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                {content}
            </Card>
        );
    }

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                '&:hover': { boxShadow: 3, transform: 'translateY(-1px)' },
            }}
        >
            <CardActionArea onClick={onClick} sx={{ height: '100%' }}>
                {content}
            </CardActionArea>
        </Card>
    );
}

function formatDateTime(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export default function DashboardPage() {
    const navigate = useNavigate();
    const username = localStorage.getItem('username') || 'User';
    const { account, commodity, location, laArea, t } = useEntityLabels();

    const summaryQ = useQuery({
        queryKey: ['loads', 'dashboardSummary'],
        queryFn: ({ signal }) => getDashboardSummary(signal),
        staleTime: 30_000,
        refetchOnWindowFocus: true,
    });

    const summary = summaryQ.data;
    const visibleShortcuts = useMemo(() => {
        const labelOverrides = {
            accounts: account.plural,
            commodities: commodity.plural,
            locations: location.plural,
            laAreas: laArea.plural,
        };
        return shortcuts
            .filter((item) => !isNavItemHidden(item.permKey))
            .map((item) => ({
                ...item,
                label: labelOverrides[item.permKey] ?? t(item.label),
                description: t(item.description),
            }));
    }, [account.plural, commodity.plural, location.plural, laArea.plural, t]);

    return (
        <FormPageScroll>
        <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
            <Stack spacing={0.5} sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <DashboardIcon sx={{ color: BRAND_RED }} />
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Dashboard
                    </Typography>
                </Stack>
                <Typography variant="body1" color="text.secondary">
                    Welcome back, {username}. Here is what is happening on site today.
                </Typography>
                {summary?.approximate && (
                    <Typography variant="caption" color="text.secondary">
                        Counts are based on the most recent 200 loads. Restart the API after deploying the dashboard summary endpoint for exact totals.
                    </Typography>
                )}
            </Stack>

            {summaryQ.isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : summaryQ.isError ? (
                <Paper sx={{ p: 3, border: '1px solid', borderColor: 'divider' }}>
                    <Typography color="error">
                        Could not load site statistics. Check that the API is running and you are signed in.
                    </Typography>
                </Paper>
            ) : (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="On site now"
                                value={summary?.incompleteTotal ?? 0}
                                subtitle="Incomplete loads in the yard"
                                icon={<PendingActionsIcon fontSize="large" />}
                                onClick={() => navigate('/loads/weighing')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Completed today"
                                value={summary?.completedToday ?? 0}
                                subtitle="Loads finished since midnight"
                                icon={<CheckCircleOutlineIcon fontSize="large" />}
                                onClick={() => navigate('/loads/completed')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Last 7 days"
                                value={summary?.completedLast7Days ?? 0}
                                subtitle="Completed loads this week"
                                icon={<TrendingUpIcon fontSize="large" />}
                                onClick={() => navigate('/loads/completed')}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <StatCard
                                title="Weighing console"
                                value="Open"
                                subtitle="Go straight to live weighing"
                                icon={<ScaleIcon fontSize="large" />}
                                onClick={() => navigate('/loads/weighing')}
                            />
                        </Grid>
                    </Grid>

                    {(summary?.incompleteByStage?.length ?? 0) > 0 && (
                        <Paper sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                                Incomplete loads by stage
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {summary.incompleteByStage.map((stage) => (
                                    <Chip
                                        key={stage.stageId}
                                        label={`${stage.stageDescription}: ${stage.count}`}
                                        sx={{ mb: 1 }}
                                    />
                                ))}
                            </Stack>
                        </Paper>
                    )}

                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, lg: 7 }}>
                            <Paper sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                    Shortcuts
                                </Typography>
                                <Grid container spacing={2}>
                                    {visibleShortcuts.map((item) => (
                                        <Grid size={{ xs: 12, sm: 6 }} key={item.path}>
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    borderRadius: 2,
                                                    height: '100%',
                                                }}
                                            >
                                                <CardActionArea onClick={() => navigate(item.path)} sx={{ height: '100%' }}>
                                                    <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                                        <Box sx={{ color: BRAND_RED, mt: 0.25 }}>{item.icon}</Box>
                                                        <Box>
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                {item.label}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {item.description}
                                                            </Typography>
                                                        </Box>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>
                        </Grid>

                        <Grid size={{ xs: 12, lg: 5 }}>
                            <Paper sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        Recent incomplete loads
                                    </Typography>
                                </Box>
                                {(summary?.recentIncomplete?.length ?? 0) === 0 ? (
                                    <Box sx={{ p: 2.5 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No incomplete loads on site right now.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Ticket</TableCell>
                                                    <TableCell>Vehicle</TableCell>
                                                    <TableCell>Stage</TableCell>
                                                    <TableCell>Updated</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {summary.recentIncomplete.map((row) => (
                                                    <TableRow
                                                        key={row.id}
                                                        hover
                                                        sx={{ cursor: 'pointer' }}
                                                        onClick={() => navigate('/loads/weighing')}
                                                    >
                                                        <TableCell>{row.ticketNumber ?? '—'}</TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                                                                {row.vehicleReg || '—'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120, display: 'block' }}>
                                                                {row.accountName || row.commodityName || ''}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>{row.stageDescription || '—'}</TableCell>
                                                        <TableCell>{formatDateTime(row.transactionDate)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                </>
            )}
        </Box>
        </FormPageScroll>
    );
}
